import {useRef} from 'react';

import {
    Alert,
    SimpleGrid,
} from '@mantine/core';
import {useNavigate} from 'react-router-dom';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';

import {useApplications} from '../hooks/useApplications.ts';
import {useApplicationsFilter} from '../hooks/useApplicationsFilter.ts';
import {useApplicationsSelection} from '../hooks/useApplicationsSelection.ts';
import {useApplicationsSearch} from '../hooks/useApplicationsSearch.ts';
import {useVerifiedPermissions} from '@flexinet/auth-components';
import {Application} from '../types';

// Token-paginated applications catalog. There is no page-based code path here
// and no offset props on the table — page size is a server concern (PAGE_SIZE
// in the query hook) and the footer states what is LOADED, never a total.
//
// History, because the old comment here was misread as live code: FNP-913
// replaced a hand-rolled Mantine <Table> (TableHeaderRow + TableBody + the
// page-based SimpleTablePagination) with the shared MantineDataTable. That
// hand-rolled table was not broken, but it structurally could not have sorting,
// token pagination, or the loading/empty/error states. FNP-560 finished the job:
// isFetchingMore is now wired to the footer, and the two empty states are
// distinct.
export const RecordsTable = () => {

    const {allowedActions} = useVerifiedPermissions();
    const navigate = useNavigate();

    const {filters} = useApplicationsFilter();

    const {
        columns,
        // The full loaded set BEFORE keyword filtering — the window the
        // footer's "N of M loaded" claim is measured against.
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchNextPage,
    } = useApplications(filters);

    const {
        keyword,
        handleFilterByKeyword,
    } = useApplicationsSearch();

    const {
        selection,
        handleRowSelection,
        handleClearSelection,
    } = useApplicationsSelection();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Selection stays gated on DeleteApplication, exactly as the hand-rolled
    // table gated its select column — the affordance is HIDDEN, not disabled,
    // when the action is not permitted.
    const canSelect = allowedActions.includes('DeleteApplication');

    const selectedRecords = canSelect
        ? visibleItems.filter(item => selection[item.id])
        : undefined;

    // Two distinct empty states: nothing exists yet, versus nothing matched the
    // search. Collapsing them into one message tells an operator who mistyped a
    // keyword that they have no applications at all — and sends them hunting
    // for a console bug when the data is simply hidden by their own filter.
    //
    // The filtered wording says "loaded so far" deliberately: under token
    // pagination the keyword only sees the pages already fetched, so a match
    // may exist on a page nobody has scrolled to yet.
    const noRecordsText = keyword
        ? `No applications match "${keyword}" in the records loaded so far.`
        : 'No applications yet.';

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            {/*
              * TableSearchForm, not SimpleTableBatchAction. The latter REQUIRES
              * `limit` + `handleItemsPerPageChange` and always renders the
              * rows-per-page dropdown — which token pagination cannot honour
              * (there is no page to resize and no total to divide).
              *
              * Nothing is lost: no caller ever passed `extraTools` or
              * `settings` (IndexPage renders a bare <RecordsTable/>), and the
              * search box is what TableSearchForm provides. It is also the
              * clearable one — the clear control resets BOTH the input and the
              * module-scoped keyword store.
              *
              * Page size is a server concern now: PAGE_SIZE in the query hook.
              */}
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title='Could not load applications'>
                    The console API did not return the application catalog. Try Reload;
                    if it persists the API may be unavailable in this environment.
                </Alert>
            )}

            <MantineDataTable<Application>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far — the same
                // scope the keyword filter works on. Token pagination cannot
                // reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={canSelect
                    ? (records) => {
                        handleClearSelection();
                        records.forEach(record => handleRowSelection(record.id, true));
                    }
                    : undefined}
                onScrollToBottom={() => fetchNextPage()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}) =>
                    navigate(`/applications/${encodeURIComponent(record.id)}`)}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'application'}
                // Distinct from the table's `fetching`: this drives the
                // footer's "loading more" state, so already-loaded rows do not
                // flash the initial-load spinner on scroll-to-bottom.
                isFetchingMore={isFetchingNextPage}
                nextToken={nextToken}
                handleLoadMore={fetchNextPage}
            />
        </SimpleGrid>
    );
};
